import React, { useState } from 'react';

/* ─── Hero ──────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative overflow-hidden hero-gradient pt-32 pb-20 md:pb-28">
      <div className="blob-1" />
      <div className="blob-2" />
      <div className="max-w-screen-xl mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row items-center gap-12">
          {/* Left — text */}
          <div className="flex-1 text-center md:text-left">
            <span
              className="inline-block text-xs font-semibold tracking-widest uppercase px-4 py-1 rounded-full mb-6"
              style={{ background: 'rgba(99,102,241,0.15)', color: '#4f46e5' }}
            >
              AI-Powered Intelligence
            </span>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-gray-900 mb-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Make Better Decisions,{' '}
              <span style={{ color: '#6366f1' }}>Every Single Time.</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto md:mx-0">
              Achieve more with intelligent analytics, real-time insights, one-click reports, and AI-driven recommendations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <a
                href="#"
                className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white px-7 py-3.5 rounded-full shadow-lg transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#111827' }}
              >
                Start for Free
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <a
                href="#"
                className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-gray-700 px-7 py-3.5 rounded-full border border-gray-300 bg-white/60 backdrop-blur hover:bg-white transition-colors"
              >
                Watch Demo
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Right — mock dashboard card */}
          <div className="flex-1 w-full max-w-lg">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-white/60">
              {/* Card header */}
              <div className="flex items-center gap-2 px-5 py-3 border-b bg-gray-50">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <span className="ml-3 text-xs text-gray-400 font-mono">orvexis — decision dashboard</span>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Decision Score</p>
                    <p className="text-3xl font-bold text-gray-900">94<span className="text-lg text-gray-400">/100</span></p>
                  </div>
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-md"
                    style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}
                  >
                    A+
                  </div>
                </div>
                {/* Progress bars */}
                {[
                  { label: 'Data Quality', pct: 92, color: '#6366f1' },
                  { label: 'Risk Assessment', pct: 87, color: '#6366f1' },
                  { label: 'AI Confidence', pct: 96, color: '#f59e0b' },
                ].map(({ label, pct, color }) => (
                  <div key={label} className="mb-3">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>{label}</span>
                      <span className="font-medium">{pct}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                ))}
                <div className="mt-4 pt-4 border-t flex gap-2">
                  {['Analytics', 'AI Report', 'Export'].map(tag => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1 rounded-full font-medium"
                      style={{ background: 'rgba(99,102,241,0.12)', color: '#4f46e5' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Trusted By ─────────────────────────────────────────────── */
function TrustedBy() {
  const companies = [
    'Google', 'Amazon', 'Microsoft', 'Apple', 'Meta', 'Salesforce', 'IBM', 'Oracle',
    'Google', 'Amazon', 'Microsoft', 'Apple', 'Meta', 'Salesforce', 'IBM', 'Oracle',
  ];
  return (
    <section className="bg-white py-12 border-y border-gray-100 overflow-hidden">
      <p className="text-center text-sm text-gray-400 font-medium tracking-wide uppercase mb-8">
        Trusted by teams at
      </p>
      <div className="relative overflow-hidden">
        <div className="marquee-track flex gap-16 whitespace-nowrap w-max">
          {companies.map((name, i) => (
            <span key={i} className="text-xl font-bold text-gray-300 hover:text-gray-400 transition-colors select-none">
              {name}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Features ───────────────────────────────────────────────── */
const features = [
  {
    title: 'Real-Time Analytics',
    description: 'Monitor every metric that matters. Get live dashboards with intelligent alerts so you never miss a signal.',
    bg: '#ede9fe',
    accent: '#6366f1',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
    ),
  },
  {
    title: 'AI Recommendations',
    description: 'Our AI engine scans your data and surfaces the most impactful actions — ranked by expected outcome.',
    bg: '#fce7f3',
    accent: '#db2777',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      </svg>
    ),
  },
  {
    title: 'Scenario Planning',
    description: 'Model what-if scenarios before committing. Stress-test strategies with AI simulations in seconds.',
    bg: '#dbeafe',
    accent: '#3b82f6',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    title: 'Clarity Score',
    description: 'Every decision gets a Clarity Score — a composite AI rating that shows how confident you should be.',
    bg: '#fdf4ff',
    accent: '#8b5cf6',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
        />
      </svg>
    ),
  },
];

function Features() {
  return (
    <section className="py-20 bg-[#f8fafc]">
      <div className="max-w-screen-xl mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Everything you need to decide with confidence
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            From raw data to clear action — Orvexis handles the complexity so you can focus on what matters.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map(({ title, description, bg, accent, icon }) => (
            <div
              key={title}
              className="rounded-2xl p-8 flex flex-col gap-4 transition-transform hover:-translate-y-1"
              style={{ backgroundColor: bg }}
            >
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: 'white', color: accent }}
              >
                {icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {title}
              </h3>
              <p className="text-gray-600 leading-relaxed">{description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Templates / Modules Showcase ──────────────────────────── */
const modules = [
  { name: 'Executive Summary', tag: 'AI' },
  { name: 'Risk Matrix', tag: 'Analytics' },
  { name: 'KPI Tracker', tag: 'Live' },
  { name: 'Forecast Model', tag: 'AI' },
  { name: 'Competitor Map', tag: 'Analytics' },
  { name: 'Budget Optimizer', tag: 'AI' },
  { name: 'Team Scorecard', tag: 'Live' },
  { name: 'Market Pulse', tag: 'Analytics' },
  { name: 'Decision Log', tag: 'Core' },
  { name: 'Goal Alignment', tag: 'Core' },
  { name: 'Data Importer', tag: 'Core' },
  { name: 'Custom Reports', tag: 'Pro' },
];

const tagColors: Record<string, string> = {
  AI: 'rgba(99,102,241,0.15)',
  Analytics: 'rgba(139,92,246,0.15)',
  Live: 'rgba(245,158,11,0.15)',
  Core: 'rgba(107,114,128,0.12)',
  Pro: 'rgba(219,39,119,0.12)',
};
const tagText: Record<string, string> = {
  AI: '#4f46e5', Analytics: '#7c3aed', Live: '#b45309', Core: '#6b7280', Pro: '#be185d',
};

function ModulesShowcase() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-screen-xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Ready-made decision modules
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Start instantly with professionally designed modules — or build your own from scratch.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {modules.map(({ name, tag }) => (
            <div
              key={name}
              className="group rounded-xl border border-gray-100 bg-gray-50 p-4 flex flex-col gap-3 hover:shadow-md hover:border-[#6366f1] transition-all cursor-pointer"
            >
              <div
                className="h-24 rounded-lg flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${tagColors[tag]}, rgba(248,250,252,0.5))` }}
              >
                <span
                  className="text-2xl font-bold"
                  style={{ color: tagText[tag], fontFamily: 'Poppins, sans-serif' }}
                >
                  {name.split(' ')[0][0]}{name.split(' ')[1]?.[0] ?? ''}
                </span>
              </div>
              <p className="text-sm font-semibold text-gray-800">{name}</p>
              <div className="flex items-center justify-between">
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ background: tagColors[tag], color: tagText[tag] }}
                >
                  {tag}
                </span>
                <button
                  className="text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ color: '#6366f1' }}
                >
                  Use →
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Import / Export ────────────────────────────────────────── */
function ImportExport() {
  return (
    <section className="py-20" style={{ background: 'linear-gradient(135deg, #ede9fe 0%, #dbeafe 100%)' }}>
      <div className="max-w-screen-xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Import */}
          <div className="bg-white/70 backdrop-blur rounded-2xl p-8 shadow-sm">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
              style={{ backgroundColor: 'rgba(99,102,241,0.15)' }}
            >
              <svg className="w-6 h-6" style={{ color: '#6366f1' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Import from anywhere
            </h3>
            <p className="text-gray-600 mb-5">
              Connect your existing tools — spreadsheets, databases, APIs — and pull data in automatically. Works with CSV, Excel, Google Sheets, Notion, and more.
            </p>
            <div className="flex flex-wrap gap-2">
              {['CSV', 'Excel', 'Google Sheets', 'Notion', 'SQL', 'API'].map(src => (
                <span key={src} className="text-xs font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-600">
                  {src}
                </span>
              ))}
            </div>
          </div>

          {/* Export */}
          <div className="bg-white/70 backdrop-blur rounded-2xl p-8 shadow-sm">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
              style={{ backgroundColor: 'rgba(99,102,241,0.12)' }}
            >
              <svg className="w-6 h-6 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Export with one click
            </h3>
            <p className="text-gray-600 mb-5">
              Share polished reports instantly. Export to PDF, Word, PowerPoint, or share a live link with your stakeholders — no design skills required.
            </p>
            <div className="flex flex-wrap gap-2">
              {['PDF', 'Word', 'PowerPoint', 'Live Link', 'Slack', 'Email'].map(fmt => (
                <span key={fmt} className="text-xs font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-600">
                  {fmt}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Social Proof / Stats ───────────────────────────────────── */
function Stats() {
  const stats = [
    { value: '50K+', label: 'Decisions made daily' },
    { value: '98%', label: 'Accuracy rate' },
    { value: '3×', label: 'Faster than manual analysis' },
    { value: '4.9★', label: 'Average user rating' },
  ];
  return (
    <section className="bg-gray-900 py-16">
      <div className="max-w-screen-xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map(({ value, label }) => (
            <div key={label}>
              <p className="text-4xl font-bold mb-2" style={{ color: '#6366f1', fontFamily: 'Poppins, sans-serif' }}>
                {value}
              </p>
              <p className="text-gray-400 text-sm">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── FAQ ────────────────────────────────────────────────────── */
const faqs = [
  {
    q: 'How do I get started with Orvexis?',
    a: 'Sign up for free — no credit card required. Once inside, you can connect your data sources or start with one of our pre-built modules in minutes.',
  },
  {
    q: 'What data sources does Orvexis support?',
    a: 'We support CSV, Excel, Google Sheets, Notion, SQL databases, and REST APIs. Our integration library is growing constantly.',
  },
  {
    q: 'Is my data secure?',
    a: 'Yes. All data is encrypted in transit and at rest. We are SOC 2 Type II compliant and never share your data with third parties.',
  },
  {
    q: 'Can I use Orvexis on mobile?',
    a: 'Orvexis is fully responsive and works on any device. Native mobile apps for iOS and Android are on our roadmap.',
  },
  {
    q: 'What happens if I need help?',
    a: 'Our support team is available via live chat, email, and our community forum. Pro and Enterprise plans include priority support with dedicated success managers.',
  },
];

function FAQ() {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <section className="py-20 bg-[#f8fafc]">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Frequently asked questions
          </h2>
          <p className="text-gray-500">Everything you need to know about Orvexis.</p>
        </div>
        <div className="flex flex-col gap-3">
          {faqs.map(({ q, a }, i) => (
            <div
              key={i}
              className="border border-gray-200 rounded-xl bg-white overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between px-6 py-5 text-left text-gray-900 font-medium hover:bg-gray-50 transition-colors"
                onClick={() => setOpen(open === i ? null : i)}
              >
                <span>{q}</span>
                <svg
                  className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${open === i ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {open === i && (
                <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                  {a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CTA Banner ─────────────────────────────────────────────── */
function CTABanner() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-screen-xl mx-auto px-6">
        <div
          className="rounded-3xl px-8 py-16 text-center relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #8b5cf6 100%)' }}
        >
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />
          <div className="relative z-10">
            <h2
              className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Ready for clarity?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
              Join thousands of teams making smarter, faster decisions with Orvexis. Start free today — no credit card needed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#"
                className="inline-flex items-center justify-center gap-2 text-sm font-bold text-gray-900 bg-white px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-shadow"
              >
                Create Free Account
              </a>
              <a
                href="#"
                className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white border border-white/40 px-8 py-4 rounded-full hover:bg-white/10 transition-colors"
              >
                Talk to Sales
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Page ───────────────────────────────────────────────────── */
export default function Home() {
  return (
    <>
      <Hero />
      <TrustedBy />
      <Features />
      <ModulesShowcase />
      <ImportExport />
      <Stats />
      <FAQ />
      <CTABanner />
    </>
  );
}
